import { CommentStatus } from '@getaccept/lib-shared/src/contextual-comments/types/comment-status';
import type { Ref } from '@vue/composition-api';
import { unref, ref } from '@vue/composition-api';
import type { Comment } from '@getaccept/lib-shared/src/contextual-comments/types/comment';
import type { MaybeRef } from '@vueuse/core';
import type { CommentEditorSelection } from '@getaccept/lib-shared/src/contextual-comments/types/comment-selection';
import type { CreateCommentDto } from '@getaccept/lib-shared/src/contextual-comments/types/create-comment-dto';
import type { UpdateCommentDto } from '@getaccept/lib-shared/src/contextual-comments/types/update-comment-dto';
import type { CreateReplyInput } from '@getaccept/lib-shared/src/contextual-comments/types/create-reply-input';
import { CommentsHelper } from '@getaccept/lib-shared/src/contextual-comments/helpers/comments.helper';
import { CommentsFactory } from '@getaccept/lib-shared/src/contextual-comments/helpers/comments.factory';
import { CommentService } from '../../api/comments/services/comment.service';

export const useContextualComments = () => {
  const comments: Ref<Comment[]> = ref([]);
  const selectedCommentId = ref('');

  const loading = ref(false);
  const loaded = ref(false);
  const loadFailed = ref(false);

  const loadComments = async (
    statuses?: MaybeRef<CommentStatus[]>,
    limit?: MaybeRef<number>,
    offset?: MaybeRef<number>
  ) => {
    try {
      loading.value = true;
      loadFailed.value = false;
      comments.value = await CommentService.load(unref(statuses), unref(limit), unref(offset));
      loaded.value = true;
    } catch (e) {
      loadFailed.value = true;
    } finally {
      loading.value = false;
    }
  };

  const createComment = async (
    editorSelection: MaybeRef<CommentEditorSelection>,
    pageId: MaybeRef<string>
  ) => {
    const payload: CreateCommentDto = {
      text: '',
      pageId: unref(pageId),
      status: CommentStatus.Draft,
      editorSelection: unref(editorSelection),
    };

    try {
      const createdComment = await CommentService.create(payload);
      comments.value = [...comments.value, createdComment];

      return createdComment;
    } catch (e) {
      return null;
    }
  };

  const updateComment = async (payload: MaybeRef<Partial<UpdateCommentDto>>) => {
    const commentPartial = unref(payload);
    const staleComment = comments.value.find(({ id }) => id === commentPartial.commentId);

    comments.value = CommentsHelper.updateComment(
      commentPartial,
      commentPartial.commentId,
      comments.value
    );

    try {
      await CommentService.update(commentPartial);
    } catch (e) {
      comments.value = CommentsHelper.updateComment(staleComment, staleComment.id, comments.value);
    }
  };

  const createReply = async (
    payload: MaybeRef<CreateReplyInput>,
    recipientId: MaybeRef<string>
  ) => {
    const createReplyInput = unref(payload);

    const participant = CommentsFactory.commentParticipant({
      recipientId: unref(recipientId),
    });

    const newReply = CommentsFactory.reply({
      createdBy: participant,
      reads: [{ participant, timestamp: Date.now() }],
      ...createReplyInput,
    });

    comments.value = CommentsHelper.addReply(newReply, createReplyInput.commentId, comments.value);

    try {
      const createdReply = await CommentService.createReply(createReplyInput);
      comments.value = CommentsHelper.updateReply(
        createdReply,
        newReply.id,
        newReply.commentId,
        comments.value
      );
    } catch (e) {
      comments.value = CommentsHelper.removeReply(newReply.id, newReply.commentId, comments.value);
    }
  };

  const readComment = async (commentId: MaybeRef<string>, recipientId: MaybeRef<string>) => {
    const readEvent = CommentsFactory.readEvent({
      participant: CommentsFactory.commentParticipant({
        recipientId: unref(recipientId),
      }),
    });

    comments.value = CommentsHelper.addRead(readEvent, unref(commentId), comments.value);

    try {
      await CommentService.readComment(unref(commentId));
    } catch (e) {
      comments.value = CommentsHelper.removeRead(readEvent, unref(commentId), comments.value);
    }
  };

  const readReply = async (
    replyId: MaybeRef<string>,
    commentId: MaybeRef<string>,
    recipientId: MaybeRef<string>
  ) => {
    const readEvent = CommentsFactory.readEvent({
      participant: CommentsFactory.commentParticipant({
        recipientId: unref(recipientId),
      }),
    });

    comments.value = CommentsHelper.addReplyRead(
      readEvent,
      unref(replyId),
      unref(commentId),
      comments.value
    );
    try {
      await CommentService.readReply(unref(replyId), unref(commentId));
    } catch (e) {
      comments.value = CommentsHelper.removeReplyRead(
        readEvent,
        unref(replyId),
        unref(commentId),
        comments.value
      );
    }
  };

  const selectComment = (commentId: MaybeRef<string>, recipientId: MaybeRef<string>) => {
    selectedCommentId.value = unref(commentId);

    const selectedComment = comments.value.find(comment => comment.id === selectedCommentId.value);

    if (!selectedComment) {
      return;
    }

    const isCommentUnread = !selectedComment.reads.find(
      ({ participant }) => participant.recipientId === unref(recipientId)
    );

    if (isCommentUnread) {
      readComment(selectedCommentId, unref(recipientId));
    }

    const unreadReplies = selectedComment.replies.filter(
      ({ reads }) =>
        !reads.find(({ participant }) => participant.recipientId === unref(recipientId))
    );

    unreadReplies.forEach(reply => readReply(reply.id, selectedComment.id, unref(recipientId)));
  };

  return {
    loading,
    loaded,
    loadFailed,
    comments,
    selectedCommentId,
    loadComments,
    createComment,
    updateComment,
    readComment,
    createReply,
    readReply,
    selectComment,
  };
};
